// --- Core ---
import {utc} from "@date-fns/utc";
import {formatISO, isDate} from "date-fns";
import {generatePath, PathParam} from "react-router";
import {compact, each, isObject, isString, pick} from "underscore";
// --- SaniQ ---
import {PaginationQuery} from "helpers/interfaces/Pagination";
import {Sorting} from "helpers/interfaces/Sorting";
import {formatDateRangeFilter} from "shared/filter/handlers/DateRange";

type TimeRange = "curM" | "lastM" | "30D" | "60D" | "90D" | "6M" | "12M" | "curY" | "lastY" | "custom";

interface TimeQuery {
  from?: Date;
  to?: Date;
  range?: TimeRange;
}

type OtherParam = Date | string | number | boolean;
type CustomValue = OtherParam | TimeQuery | Record<string, OtherParam>;
type Queries = {
  pagination?: PaginationQuery;
  sorting?: Sorting;
  time?: TimeQuery;
  filters?: string;
  custom?: Record<string, CustomValue | Array<OtherParam>>;
};
type Params<Path extends string> = {
  [key in PathParam<Path>]: string | null
};

const isTimeQuery = (value: CustomValue): value is TimeQuery => isObject(value) && !isDate(value) && !!(value.range || value.from || value.to);

/**
 * Generate URL based on path, parameters and queries
 *
 * Queries are automatically formatted to the respective query parameters.
 * Custom queries are used as is for the key, but the value is converted to string.
 * Undefined custom queries will be ignored, but null values are converted to 'null'
 *
 * @remarks based on generatePath by React Router
 *
 * @param originalPath The original path as passed to generatePath
 * @param params The path params as passed to generatePath
 * @param queries Query parameters to build the search params with special handling of filters, pagination and sorting
 * @return The formatted URL
 */
function generateUrl<Path extends string = string>(
  originalPath: Path,
  params: Params<Path> = undefined,
  queries: Queries = {}
): string {
  const {filters, pagination, sorting, time, custom = {}} = queries;
  const path = generatePath(originalPath, params);

  const searchParams = new URLSearchParams({
    // Builtin Query Params
    ...pick({
      // Pagination Params
      page: pagination?.page.toString(),
      per_page: pagination?.perPage.toString(),
      // Sorting Params
      order: sorting?.order,
      query: sorting?.query,
      sort: sorting?.sort,
      // Time Params
      time_from: time?.from && formatISO(time.from, {in: utc}),
      time_to: time?.to && formatISO(time.to, {in: utc})
    }, isString)
  });

  const arrayParams: string[] = [];

  // Additional Query Params
  each(custom, (value, key) => {
    if (Array.isArray(value)) {
      each(value, (nestedValue) => {
        //NOTE(Keune): URLSearchParams does not support bracket notation but rails requires it
        const formattedValue = customParamToString(nestedValue);

        if (formattedValue) {
          arrayParams.push(`${key}[]=${formattedValue}`);
        }
      });
    } else if (isTimeQuery(value)){
      // @ts-expect-error formatDateRangeFilter only requires id, from and to
      arrayParams.push(formatDateRangeFilter({id: key, ...value}));
    } else if (!isDate(value) && isObject(value)) {
      //NOTE(Keune): isObject is true for everything that is not a primitive: arrays, functions, dates
      each(value, (nestedValue, nestedKey) => {
        //NOTE(Keune): URLSearchParams does not support bracket notation but rails requires it
        const formattedValue = customParamToString(nestedValue);

        if (formattedValue) {
          arrayParams.push(`${key}[${nestedKey}]=${formattedValue}`);
        }
      });
    } else {
      const formattedValue = customParamToString(value);

      if (formattedValue) {
        searchParams.append(key, formattedValue);
      }
    }
  });

  const formattedParams = searchParams.toString();

  if (formattedParams.length || arrayParams.length || filters) {
    return path + '?' + compact([formattedParams, ...arrayParams, filters]).join('&');
  } else {
    return path;
  }
}

/**
 * Format a custom query parameter to string. Ignores undefined values but transforms null to string
 */
function customParamToString(value: OtherParam): string {
  if (value === undefined) {
    return undefined;
  } else if (value === null) {
    return 'null';
  } else if (isDate(value)) {
    return formatISO(value, {in: utc});
  } else {
    return value.toString();
  }
}

export {
  generateUrl,
  Queries,
  TimeQuery,
  TimeRange
};
